import type { Rng } from '../../core/rng';
import type { MiniGameKind } from './data';

/**
 * Skill-based gathering mini-games as pure, time-stepped state machines.
 * The overlay feeds them input (`press`/`release`/`pick`) and frame time
 * (`update`); tests drive them the same way with a seeded Rng.
 */

export interface MiniGame {
  readonly kind: MiniGameKind;
  /** True once the attempt is over (read `quality`/`failed`). */
  done: boolean;
  update(dt: number): void;
  /** Key/button pressed (G). */
  press(): void;
  /** Key/button released. */
  release(): void;
  /** Performance 0..1 (meaningful once done). */
  quality(): number;
  /** The attempt yields nothing (spooked or escaped fish). */
  failed(): boolean;
  /** The attempt guarantees the rare material. */
  forceRare(): boolean;
  /** Short grade shown with the results ("Perfect", "Clean", "Rough"). */
  grade(): string;
}

/** Brief pause between rounds so the player can read the feedback. */
export const ROUND_PAUSE = 0.55;

const gradeFromQuality = (q: number): string => (q >= 0.95 ? 'Perfect' : q >= 0.7 ? 'Great' : q >= 0.4 ? 'Clean' : 'Rough');

// ================================================================ foraging: ring tap

export type TapGrade = 'perfect' | 'good' | 'miss';
export const RING_PERFECT = 0.035;
export const RING_GOOD = 0.09;
const TAP_VALUE: Record<TapGrade, number> = { perfect: 1, good: 0.6, miss: 0 };

/** Grades a tap by how far the shrinking ring (radius 0..1) is from the target ring. */
export function scoreRingTap(radius: number, target: number): TapGrade {
  const d = Math.abs(radius - target);
  return d <= RING_PERFECT ? 'perfect' : d <= RING_GOOD ? 'good' : 'miss';
}

/** Foraging: press when the shrinking ring overlaps the target ring, three times. */
export class RingTapGame implements MiniGame {
  readonly kind = 'ringtap' as const;
  readonly rounds: number;
  round = 0;
  /** Time into the current round. */
  t = 0;
  duration = 1.7;
  target = 0.5;
  results: TapGrade[] = [];
  /** Radius at the last tap (for the feedback flash). */
  lastRadius = 0;
  pause = 0;
  done = false;
  private readonly speed: number;

  constructor(
    private rng: Rng,
    opts: { rounds?: number; speed?: number } = {},
  ) {
    this.rounds = opts.rounds ?? 3;
    this.speed = opts.speed ?? 1;
    this.setupRound();
  }

  get radius(): number {
    return Math.max(0, 1 - this.t / this.duration);
  }

  get lastGrade(): TapGrade | null {
    return this.results[this.results.length - 1] ?? null;
  }

  private setupRound(): void {
    this.t = 0;
    this.duration = (1.7 - this.round * 0.22) / this.speed;
    this.target = this.rng.range(0.32, 0.6);
  }

  private record(g: TapGrade): void {
    this.lastRadius = this.radius;
    this.results.push(g);
    this.pause = ROUND_PAUSE;
  }

  update(dt: number): void {
    if (this.done) return;
    if (this.pause > 0) {
      this.pause -= dt;
      if (this.pause <= 0) {
        this.pause = 0;
        this.round++;
        if (this.round >= this.rounds) this.done = true;
        else this.setupRound();
      }
      return;
    }
    this.t += dt;
    if (this.radius <= 0) this.record('miss');
  }

  press(): void {
    if (this.done || this.pause > 0) return;
    this.record(scoreRingTap(this.radius, this.target));
  }

  release(): void {}

  quality(): number {
    return this.results.reduce((s, g) => s + TAP_VALUE[g], 0) / this.rounds;
  }

  failed(): boolean {
    return false;
  }

  forceRare(): boolean {
    return this.results.length === this.rounds && this.results.every((g) => g === 'perfect');
  }

  grade(): string {
    return gradeFromQuality(this.quality());
  }
}

// ================================================================ logging & mining: hold bar

export type SwingGrade = 'perfect' | 'success' | 'weak' | 'miss';
const SWING_VALUE: Record<SwingGrade, number> = { perfect: 1, success: 0.75, weak: 0.3, miss: 0 };

/**
 * Grades a released swing: inside the perfect core, inside the success zone,
 * a weak swing (released early but with some force), or a miss (feeble tap or
 * an overswing that filled the whole bar).
 */
export function scoreRelease(fill: number, zone: readonly [number, number], core: readonly [number, number]): SwingGrade {
  if (fill >= 1) return 'miss';
  if (fill >= core[0] && fill <= core[1]) return 'perfect';
  if (fill >= zone[0] && fill <= zone[1]) return 'success';
  return fill >= 0.3 ? 'weak' : 'miss';
}

export interface HoldBarOpts {
  swings: number;
  /** Base fill speed per second (the bar accelerates as it fills). */
  speed: number;
  /** Width of the success zone (0..1 of the bar). */
  zoneWidth: number;
}

export const LOGGING_BAR: HoldBarOpts = { swings: 3, speed: 0.52, zoneWidth: 0.17 };
export const MINING_BAR: HoldBarOpts = { swings: 2, speed: 0.62, zoneWidth: 0.14 };

/** Logging/Mining: hold to fill the swing bar, release inside the success zone. */
export class HoldBarGame implements MiniGame {
  readonly kind = 'holdbar' as const;
  readonly swings: number;
  swing = 0;
  fill = 0;
  holding = false;
  zone: [number, number] = [0.6, 0.76];
  core: [number, number] = [0.66, 0.7];
  results: SwingGrade[] = [];
  /** Fill at the last release (for feedback). */
  lastFill = 0;
  pause = 0;
  done = false;
  private readonly speed: number;
  private readonly zoneWidth: number;

  constructor(
    private rng: Rng,
    opts: HoldBarOpts = LOGGING_BAR,
  ) {
    this.swings = opts.swings;
    this.speed = opts.speed;
    this.zoneWidth = opts.zoneWidth;
    this.setupSwing();
  }

  get lastGrade(): SwingGrade | null {
    return this.results[this.results.length - 1] ?? null;
  }

  private setupSwing(): void {
    this.fill = 0;
    this.holding = false;
    const w = this.zoneWidth * (1 - this.swing * 0.08);
    const lo = this.rng.range(0.52, 0.9 - w);
    this.zone = [lo, lo + w];
    const cw = Math.max(0.04, w * 0.34);
    const mid = lo + w / 2;
    this.core = [mid - cw / 2, mid + cw / 2];
  }

  /** Fill rate at the current fill (accelerating). */
  rate(fill = this.fill): number {
    return this.speed * (0.75 + 0.7 * fill);
  }

  private finishSwing(): void {
    this.lastFill = this.fill;
    this.results.push(scoreRelease(this.fill, this.zone, this.core));
    this.holding = false;
    this.pause = ROUND_PAUSE;
  }

  update(dt: number): void {
    if (this.done) return;
    if (this.pause > 0) {
      this.pause -= dt;
      if (this.pause <= 0) {
        this.pause = 0;
        this.swing++;
        if (this.swing >= this.swings) this.done = true;
        else this.setupSwing();
      }
      return;
    }
    if (!this.holding) return;
    this.fill = Math.min(1, this.fill + this.rate() * dt);
    if (this.fill >= 1) this.finishSwing();
  }

  press(): void {
    if (this.done || this.pause > 0 || this.holding) return;
    this.fill = 0;
    this.holding = true;
  }

  release(): void {
    if (!this.holding || this.done) return;
    this.finishSwing();
  }

  quality(): number {
    return this.results.reduce((s, g) => s + SWING_VALUE[g], 0) / this.swings;
  }

  failed(): boolean {
    return false;
  }

  forceRare(): boolean {
    return this.results.length === this.swings && this.results.every((g) => g === 'perfect');
  }

  grade(): string {
    return gradeFromQuality(this.quality());
  }
}

// ================================================================ fishing

export const BITE_MIN = 1.5;
export const BITE_MAX = 5;
export const REACT_WINDOW = 0.6;
/** Continuous seconds the tension may stay outside the zone before the fish escapes. */
export const ESCAPE_TIME = 1.4;

export type FishPhase = 'waiting' | 'bite' | 'reel' | 'done';
export type FishOutcome = 'caught' | 'early' | 'late' | 'escaped';

/** Classifies a press relative to the bite: before it, inside the reaction window, or too late. */
export function biteReaction(t: number, biteAt: number): 'early' | 'hooked' | 'late' {
  if (t < biteAt) return 'early';
  return t - biteAt <= REACT_WINDOW ? 'hooked' : 'late';
}

/**
 * Fishing: wait for the "!" (1.5–5 s), press within 0.6 s, then keep the line
 * tension inside a drifting zone by holding (tighten) and releasing (slacken)
 * until the catch meter fills. Rare fish are decided at the hook and fight harder.
 */
export class FishingGame implements MiniGame {
  readonly kind = 'fishing' as const;
  phase: FishPhase = 'waiting';
  outcome: FishOutcome | null = null;
  t = 0;
  readonly biteAt: number;
  reaction = 0;
  rare = false;
  tension = 0.5;
  holding = false;
  zoneCenter = 0.5;
  zoneWidth = 0.26;
  zoneVel = 0;
  private retarget = 0;
  progress = 0;
  /** Continuous time outside the zone. */
  outside = 0;
  insideTime = 0;
  reelTime = 0;
  done = false;
  private readonly rareChance: number;

  constructor(
    private rng: Rng,
    opts: { rareChance?: number } = {},
  ) {
    this.rareChance = opts.rareChance ?? 0.1;
    this.biteAt = rng.range(BITE_MIN, BITE_MAX);
  }

  get inZone(): boolean {
    return Math.abs(this.tension - this.zoneCenter) <= this.zoneWidth / 2;
  }

  private end(outcome: FishOutcome): void {
    this.outcome = outcome;
    this.phase = 'done';
    this.done = true;
    this.holding = false;
  }

  update(dt: number): void {
    if (this.done) return;
    this.t += dt;
    if (this.phase === 'waiting') {
      if (this.t >= this.biteAt) this.phase = 'bite';
      return;
    }
    if (this.phase === 'bite') {
      if (this.t - this.biteAt > REACT_WINDOW) this.end('late');
      return;
    }
    // Reeling.
    this.reelTime += dt;
    this.tension = Math.max(0, Math.min(1, this.tension + (this.holding ? 0.95 : -0.8) * dt));
    this.retarget -= dt;
    if (this.retarget <= 0) {
      const speed = this.rng.range(0.12, 0.42) * (this.rare ? 1.4 : 1);
      this.zoneVel = this.rng.chance(0.5) ? speed : -speed;
      this.retarget = this.rng.range(0.7, 1.5);
    }
    const half = this.zoneWidth / 2;
    this.zoneCenter += this.zoneVel * dt;
    if (this.zoneCenter < half) {
      this.zoneCenter = half;
      this.zoneVel = Math.abs(this.zoneVel);
    } else if (this.zoneCenter > 1 - half) {
      this.zoneCenter = 1 - half;
      this.zoneVel = -Math.abs(this.zoneVel);
    }
    if (this.inZone) {
      this.outside = 0;
      this.insideTime += dt;
      this.progress = Math.min(1, this.progress + (this.rare ? 0.17 : 0.21) * dt);
      if (this.progress >= 1) this.end('caught');
    } else {
      this.outside += dt;
      this.progress = Math.max(0, this.progress - 0.05 * dt);
      if (this.outside > ESCAPE_TIME) this.end('escaped');
    }
  }

  press(): void {
    if (this.done) return;
    if (this.phase === 'waiting') {
      this.end('early');
      return;
    }
    if (this.phase === 'bite') {
      this.reaction = Math.max(0, this.t - this.biteAt);
      const bonus = 0.1 * (1 - this.reaction / REACT_WINDOW);
      this.rare = this.rng.next() < this.rareChance + bonus;
      this.zoneWidth = this.rare ? 0.2 : 0.26;
      this.phase = 'reel';
      this.holding = true;
      return;
    }
    this.holding = true;
  }

  release(): void {
    if (this.phase === 'reel') this.holding = false;
  }

  quality(): number {
    if (this.outcome !== 'caught') return 0;
    const react = 1 - Math.min(1, this.reaction / REACT_WINDOW);
    const control = this.reelTime > 0 ? this.insideTime / this.reelTime : 1;
    return Math.min(1, 0.5 + 0.3 * react + 0.2 * control);
  }

  failed(): boolean {
    return this.done && this.outcome !== 'caught';
  }

  forceRare(): boolean {
    return this.outcome === 'caught' && this.rare;
  }

  grade(): string {
    switch (this.outcome) {
      case 'early':
        return 'Spooked';
      case 'late':
        return 'Too slow';
      case 'escaped':
        return 'Got away';
      default:
        return gradeFromQuality(this.quality());
    }
  }
}

// ================================================================ excavating

export type DigHint = 'found' | 'hot' | 'warm' | 'cold';
export interface Cell {
  x: number;
  y: number;
}

export const DIG_SIZE = 5;
export const DIG_TURNS = 3;
export const DIG_OFFERS = 3;
const HINT_VALUE: Record<DigHint, number> = { found: 1, hot: 0.65, warm: 0.4, cold: 0.15 };

export const cellDist = (a: Cell, b: Cell): number => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

/** Hot/cold reading of a dug cell: the relic itself, adjacent (hot), two away (warm) or further (cold). */
export function digHint(cell: Cell, relic: Cell): DigHint {
  const d = cellDist(cell, relic);
  return d === 0 ? 'found' : d === 1 ? 'hot' : d === 2 ? 'warm' : 'cold';
}

/**
 * Excavating: a buried relic hides in a 5×5 dig site. Each of 3 turns offers
 * 3 dig spots; digging reveals a hot/warm/cold reading. From the second turn
 * one offered spot is always closer than your best reading so far, so reading
 * the hints pays off.
 */
export class DigGame implements MiniGame {
  readonly kind = 'dig' as const;
  readonly size = DIG_SIZE;
  readonly turns = DIG_TURNS;
  readonly relic: Cell;
  turn = 0;
  dug: (Cell & { hint: DigHint })[] = [];
  offers: Cell[] = [];
  found = false;
  pause = 0;
  done = false;

  constructor(private rng: Rng) {
    this.relic = { x: rng.int(0, DIG_SIZE - 1), y: rng.int(0, DIG_SIZE - 1) };
    this.offers = this.makeOffers();
  }

  private undug(): Cell[] {
    const out: Cell[] = [];
    for (let y = 0; y < this.size; y++) for (let x = 0; x < this.size; x++) if (!this.dug.some((d) => d.x === x && d.y === y)) out.push({ x, y });
    return out;
  }

  /** Best (closest) distance reached so far, or Infinity before the first dig. */
  bestDist(): number {
    return this.dug.reduce((m, d) => Math.min(m, cellDist(d, this.relic)), Infinity);
  }

  private makeOffers(): Cell[] {
    const opening = this.dug.length === 0;
    // The opening turn never offers the relic itself (no free jackpots).
    const pool = this.undug().filter((c) => !opening || cellDist(c, this.relic) > 0);
    const out: Cell[] = [];
    const take = (c: Cell) => {
      out.push(c);
      const i = pool.findIndex((p) => p.x === c.x && p.y === c.y);
      if (i >= 0) pool.splice(i, 1);
    };
    if (opening) {
      // Three spread-out spots.
      for (const c of this.rng.shuffle([...pool])) {
        if (out.length >= DIG_OFFERS) break;
        if (out.every((o) => cellDist(o, c) >= 2)) take(c);
      }
    } else {
      const best = this.bestDist();
      const closer = pool.filter((c) => cellDist(c, this.relic) < best);
      if (closer.length) take(this.rng.pick(closer));
    }
    this.rng.shuffle(pool);
    while (out.length < DIG_OFFERS && pool.length) take(pool[0] as Cell);
    return this.rng.shuffle(out);
  }

  /** Digs the offered spot `i` (0..2). */
  pick(i: number): void {
    if (this.done || this.pause > 0) return;
    const c = this.offers[i];
    if (!c) return;
    const hint = digHint(c, this.relic);
    this.dug.push({ ...c, hint });
    this.offers = [];
    if (hint === 'found') this.found = true;
    this.pause = ROUND_PAUSE + 0.25;
  }

  update(dt: number): void {
    if (this.done || this.pause <= 0) return;
    this.pause -= dt;
    if (this.pause > 0) return;
    this.pause = 0;
    this.turn++;
    if (this.found || this.turn >= this.turns) this.done = true;
    else this.offers = this.makeOffers();
  }

  press(): void {}
  release(): void {}

  quality(): number {
    if (this.found) return 1;
    const best = this.dug.reduce<DigHint>((b, d) => (HINT_VALUE[d.hint] > HINT_VALUE[b] ? d.hint : b), 'cold');
    return HINT_VALUE[best];
  }

  failed(): boolean {
    return false;
  }

  forceRare(): boolean {
    return this.found;
  }

  grade(): string {
    if (this.found) return 'Relic unearthed';
    const q = this.quality();
    return q >= 0.65 ? 'So close' : q >= 0.4 ? 'Warm trail' : 'Cold ground';
  }
}
